"use client";

import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { t } from "@/lib/i18n/interpolate";
import {
  type Interval,
  type MeanSummary,
  meanInterval,
  type ProportionIntervals,
  proportionIntervals,
  welchInterval,
} from "@/lib/tool-kit/stats-distributions/confidence-interval";
import {
  fixed,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-distributions/format";

/*
 * The result panels of the confidence-interval calculator, one per parameter. Split
 * from the widget only to keep each file readable; every word still comes from the
 * widget's own strings, passed in as `strings`.
 */

export interface CiResultStrings {
  interval: string;
  meanLabel: string;
  difference: string;
  margin: string;
  standardError: string;
  criticalT: string;
  criticalZ: string;
  workingHeading: string;
  interpretationMean: string;
  interpretationDifference: string;
  excludesZero: string;
  includesZero: string;
  interpretationProportion: string;
  methodColumn: string;
  lowerColumn: string;
  upperColumn: string;
  widthColumn: string;
  methodWald: string;
  methodWilson: string;
  methodExact: string;
  proportionSummary: string;
  clippedNote: string;
  waldWarning: string;
  apaHeading: string;
  /** "{level}% CI [{lower}, {upper}]" — APA's abbreviation, which other languages translate. */
  apaInterval: string;
  copy: string;
  copied: string;
  enterProportion: string;
  enterDifference: string;
}

const LEADING_ZERO = /^0(?=\D)/;

const num = (fmt: NumberFormatter, value: number, digits = 4) =>
  fmt(value, { maximumFractionDigits: digits });

function apaInterval(
  template: string,
  level: number,
  lower: number,
  upper: number,
  fmt: NumberFormatter,
  proportion = false
) {
  const decimals = proportion ? 3 : 2;
  return t(template, {
    level: fmt(level * 100, { maximumFractionDigits: 2 }),
    lower: fixed(lower, decimals, fmt, !proportion),
    upper: fixed(upper, decimals, fmt, !proportion),
  });
}

function ApaLine({
  text,
  strings,
}: {
  text: string;
  strings: CiResultStrings;
}) {
  return (
    <div className={`${CARD} flex flex-wrap items-center gap-3`}>
      <p className={HINT}>{strings.apaHeading}</p>
      <p className="font-mono text-foreground">{text}</p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={text}
      />
    </div>
  );
}

function IntervalCards({
  interval,
  level,
  strings,
  fmt,
  estimateLabel,
}: {
  interval: Interval;
  level: number;
  strings: CiResultStrings;
  fmt: NumberFormatter;
  estimateLabel: string;
}) {
  const pct = fmt(level * 100, { maximumFractionDigits: 2 });
  return (
    <>
      <div className={`${RESULT_CARD} text-center`}>
        <p className="font-semibold text-2xl text-foreground">
          {num(fmt, interval.lower)} – {num(fmt, interval.upper)}
        </p>
        <p className="mt-1 text-muted-foreground text-sm">
          {t(strings.interval, { level: pct })}
        </p>
      </div>
      <StatGrid>
        <StatCard label={estimateLabel} value={num(fmt, interval.estimate)} />
        <StatCard
          label={strings.margin}
          value={`± ${num(fmt, interval.margin)}`}
        />
        <StatCard label={strings.standardError} value={num(fmt, interval.se)} />
        <StatCard
          label={
            interval.df === undefined
              ? strings.criticalZ
              : t(strings.criticalT, { df: num(fmt, interval.df, 2) })
          }
          value={num(fmt, interval.critical)}
        />
      </StatGrid>
    </>
  );
}

export function MeanResult({
  summary,
  sigmaKnown,
  level,
  prompt,
  strings,
  fmt,
}: {
  summary: MeanSummary;
  sigmaKnown: boolean;
  level: number;
  /** What to ask for when the summary cannot make an interval. */
  prompt: string;
  strings: CiResultStrings;
  fmt: NumberFormatter;
}) {
  const interval = meanInterval(summary, level, sigmaKnown);
  if (!interval) {
    return <Notice>{prompt}</Notice>;
  }
  const pct = fmt(level * 100, { maximumFractionDigits: 2 });
  const symbol = interval.df === undefined ? "z*" : "t*";
  const spread = sigmaKnown ? "σ" : "s";
  return (
    <>
      <IntervalCards
        estimateLabel={strings.meanLabel}
        fmt={fmt}
        interval={interval}
        level={level}
        strings={strings}
      />
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm">
          SE = {spread} / √n = {num(fmt, summary.sd)} / √
          {num(fmt, summary.n, 0)} = {num(fmt, interval.se)}
        </p>
        <p className="font-mono text-muted-foreground text-sm">
          x̄ ± {symbol} × SE = {num(fmt, summary.mean)} ±{" "}
          {num(fmt, interval.critical)} × {num(fmt, interval.se)} ={" "}
          {num(fmt, summary.mean)} ± {num(fmt, interval.margin)}
        </p>
      </div>
      <p className={`${RESULT_CARD} text-foreground text-sm`}>
        {t(strings.interpretationMean, {
          level: pct,
          lower: num(fmt, interval.lower),
          upper: num(fmt, interval.upper),
        })}
      </p>
      <ApaLine
        strings={strings}
        text={apaInterval(
          strings.apaInterval,
          level,
          interval.lower,
          interval.upper,
          fmt
        )}
      />
    </>
  );
}

export function ProportionResult({
  x,
  n,
  level,
  strings,
  fmt,
}: {
  x: number | null;
  n: number | null;
  level: number;
  strings: CiResultStrings;
  fmt: NumberFormatter;
}) {
  const result =
    x === null || n === null ? null : proportionIntervals(x, n, level);
  if (!result || x === null || n === null) {
    return <Notice>{strings.enterProportion}</Notice>;
  }
  const smallCounts = result.successes < 10 || result.failures < 10;
  return (
    <>
      <p className="font-mono text-muted-foreground text-sm">
        {t(strings.proportionSummary, {
          x: num(fmt, x, 0),
          n: num(fmt, n, 0),
          phat: num(fmt, result.phat),
          z: num(fmt, result.z),
        })}
      </p>
      <DataTable
        head={[
          strings.methodColumn,
          strings.lowerColumn,
          strings.upperColumn,
          strings.widthColumn,
        ]}
        rows={[
          [strings.methodWilson, result.wilson],
          [strings.methodExact, result.exact],
          [strings.methodWald, result.wald],
        ].map(([label, bounds]) => {
          const { lower, upper } = bounds as ProportionIntervals["wald"];
          return [
            label as string,
            num(fmt, lower),
            num(fmt, upper),
            num(fmt, upper - lower),
          ];
        })}
      />
      {smallCounts ? <Notice>{strings.waldWarning}</Notice> : null}
      {result.wald.clipped ? <Notice>{strings.clippedNote}</Notice> : null}
      <p className={`${RESULT_CARD} text-foreground text-sm`}>
        {t(strings.interpretationProportion, {
          level: fmt(level * 100, { maximumFractionDigits: 2 }),
          lower: num(fmt, result.wilson.lower),
          upper: num(fmt, result.wilson.upper),
        })}
      </p>
      <ApaLine
        strings={strings}
        text={apaInterval(
          strings.apaInterval,
          level,
          result.wilson.lower,
          result.wilson.upper,
          fmt,
          true
        )}
      />
    </>
  );
}

export function DifferenceResult({
  first,
  second,
  level,
  strings,
  fmt,
}: {
  first: MeanSummary | null;
  second: MeanSummary | null;
  level: number;
  strings: CiResultStrings;
  fmt: NumberFormatter;
}) {
  const interval = first && second ? welchInterval(first, second, level) : null;
  if (!(interval && first && second)) {
    return <Notice>{strings.enterDifference}</Notice>;
  }
  const alpha = fmt(1 - level, { maximumFractionDigits: 4 }).replace(
    LEADING_ZERO,
    ""
  );
  const excludesZero = interval.lower > 0 || interval.upper < 0;
  return (
    <>
      <IntervalCards
        estimateLabel={strings.difference}
        fmt={fmt}
        interval={interval}
        level={level}
        strings={strings}
      />
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm">
          SE = √(s₁²/n₁ + s₂²/n₂) = √({num(fmt, first.sd)}²/{first.n} +{" "}
          {num(fmt, second.sd)}²/{second.n}) = {num(fmt, interval.se)}
        </p>
        <p className="font-mono text-muted-foreground text-sm">
          (x̄₁ − x̄₂) ± t* × SE = {num(fmt, interval.estimate)} ±{" "}
          {num(fmt, interval.critical)} × {num(fmt, interval.se)}
        </p>
      </div>
      <div className={`${RESULT_CARD} space-y-2 text-foreground text-sm`}>
        <p>
          {t(strings.interpretationDifference, {
            lower: num(fmt, interval.lower),
            upper: num(fmt, interval.upper),
            level: fmt(level * 100, { maximumFractionDigits: 2 }),
          })}
        </p>
        <p>
          {t(excludesZero ? strings.excludesZero : strings.includesZero, {
            alpha,
          })}
        </p>
      </div>
      <ApaLine
        strings={strings}
        text={apaInterval(
          strings.apaInterval,
          level,
          interval.lower,
          interval.upper,
          fmt
        )}
      />
    </>
  );
}
